import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../context/AuthContext';
import { useSupportTicketResolved } from '../../context/SupportTicketResolvedContext';
import { fetchMyTickets, formatTicketNumber } from '../../services/supportTicketService';

const cleanTitle = (t) => t?.replace(/^\[(bug|suggestion|question|other)\]\s*/i, '') ?? t;

const STATUS = {
  open: { label: 'Open', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-400' },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-400' },
  resolved: { label: 'Resolved', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  closed: { label: 'Closed', bg: 'bg-slate-100 dark:bg-slate-700', text: 'text-slate-500 dark:text-slate-400', dot: 'bg-slate-300' },
};

const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.open;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${s.bg} ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function SupportHistoryModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const { openResolvedTicket } = useSupportTicketResolved();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchMyTickets(user.id)
      .then((data) => {
        if (!cancelled) setTickets(data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load support history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, user?.id]);

  const handleOpenTicket = (ticketId) => {
    onClose();
    openResolvedTicket(ticketId);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[min(85vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-700">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Support History</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">Issues you have reported</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
              <Icon name="Loader2" size={28} className="animate-spin text-primary" />
              <span className="text-sm">Loading your tickets…</span>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && tickets.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Icon name="MessageCircle" size={26} className="text-primary" />
              </div>
              <p className="text-sm font-medium text-foreground">No support tickets yet</p>
              <p className="max-w-xs text-xs text-muted-foreground">
                Use the Contact Support button to report an issue. Your conversations will appear here.
              </p>
            </div>
          )}

          {!loading && !error && tickets.length > 0 && (
            <ul className="space-y-2">
              {tickets.map((ticket) => (
                <li key={ticket.id}>
                  <button
                    type="button"
                    onClick={() => handleOpenTicket(ticket.id)}
                    className="group w-full rounded-xl border border-slate-100 bg-white px-4 py-3 text-left transition-all hover:border-primary/30 hover:bg-primary/5 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-primary/40 dark:hover:bg-primary/10"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-[11px] font-bold text-primary">
                            {formatTicketNumber(ticket.id)}
                          </span>
                          <StatusPill status={ticket.status} />
                        </div>
                        <p className="mt-1 truncate text-sm font-semibold text-foreground group-hover:text-primary">
                          {cleanTitle(ticket.title) || 'Support request'}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{fmtDate(ticket.created_at)}</p>
                      </div>
                      <Icon
                        name="ChevronRight"
                        size={18}
                        className="mt-1 shrink-0 text-slate-300 transition-colors group-hover:text-primary dark:text-slate-600"
                      />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
